import * as PIXI from "pixi.js";
import { FEATURES_CONFIG } from "../featuresConfig";
import { COIN_SYMBOL_IDS } from "../SymbolData";
import { Settings } from "../settings";

const UI_THEME = Settings.APP.UI_THEME;

/**
 * FontGenerator
 *
 * Generates optimally-sized BitmapFont variants at runtime from a high-res
 * source font. Produces pixel-perfect glyphs by GPU-rendering each character
 * at the exact target size, avoiding PixiJS's blurry texture scaling.
 *
 * Supports two operations:
 *   - generateScaled(): Produce a crisp font at a specific pixel size
 *   - generateBlurred(): Produce a blurred font for spin animation
 *
 * Usage:
 *   const gen = new FontGenerator(renderer);
 *   gen.generateScaled("font_secondary", "font_secondary_scaled", 34);
 *   gen.generateBlurred("font_secondary", "font_secondary_blurred", 34);
 */
class FontGenerator {
  /**
   * One-time initialization. Generates all font variants needed by the game.
   * Call once when renderer is available.
   */
  static initialize(renderer) {
    if (FontGenerator._initialized) return;
    FontGenerator._initialized = true;

    // Secondary theme uses styled PIXI.Text exclusively — no BitmapFonts are loaded,
    // so scaled variant generation would crash with "source font not found".
    if (UI_THEME !== "primary") return;

    const gen = new FontGenerator(renderer);

    // Generate pixel-perfect scaled variant of secondary font for coin values
    // Only needed when coin symbols exist (font_secondary asset is conditionally loaded)
    if (COIN_SYMBOL_IDS.length > 0) {
      try {
        gen.generateScaled("font_secondary", "font_secondary_scaled", 30);
      } catch (err) {
        console.warn("[FontGenerator] Secondary font scaling failed:", err);
      }

      // Generate blurred variant of secondary font (for spin animation)
      if (FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION?.ENABLED) {
        try {
          gen.generateBlurred("font_secondary", "font_secondary_blurred", 30);
        } catch (err) {
          console.warn("[FontGenerator] Blurred font generation failed:", err);
        }
      }
    }

    // Generate scaled variant of primary font for jackpots
    try {
      gen.generateScaled("font_primary", "font_primary_small", 26);
    } catch (err) {
      console.warn("[FontGenerator] Primary font scaling failed:", err);
    }
  }

  constructor(renderer) {
    this.renderer = renderer;
  }

  /**
   * Generate a scaled BitmapFont at an exact target pixel size.
   * Each glyph is GPU-rendered at the target scale for pixel-perfect results.
   *
   * @param {string} sourceFontName - Loaded source font (e.g., "font_secondary")
   * @param {string} targetFontName - Name to register the new font as
   * @param {number} targetSize - Desired font size in pixels
   */
  generateScaled(sourceFontName, targetFontName, targetSize) {
    if (PIXI.BitmapFont.available[targetFontName]) return;

    const sourceFont = PIXI.BitmapFont.available[sourceFontName];
    if (!sourceFont) {
      console.warn(
        `[FontGenerator] Source font "${sourceFontName}" not found!`,
      );
      return;
    }

    const pageKeys = Object.keys(sourceFont.pageTextures);
    if (pageKeys.length === 0) return;
    const sourceTexture = sourceFont.pageTextures[pageKeys[0]];
    if (!sourceTexture || sourceTexture.width <= 0) return;

    // Scale based on font.size so all metrics (xAdvance, xOffset, etc.) stay consistent
    const scale = targetSize / sourceFont.size;

    // Render each glyph at the target scale using multi-pass downscaling
    const { atlasTexture, atlasWidth, atlasHeight, charEntries } =
      this._renderGlyphsToAtlas(sourceFont, scale);

    if (!atlasTexture) return;

    // Install with size=targetSize so fontSize=targetSize renders 1:1
    this._installFont(
      targetFontName,
      targetSize,
      Math.round(sourceFont.lineHeight * scale),
      atlasWidth,
      atlasHeight,
      charEntries,
      atlasTexture,
    );
  }

  /**
   * Generate a blurred BitmapFont for spin animation.
   * Each glyph is scaled down and blurred within its bounds (same approach
   * as symbol texture blur). Uses BLUR_STRENGTH × 0.25 for font clarity.
   *
   * @param {string} sourceFontName - Loaded source font
   * @param {string} targetFontName - Name to register the blurred font as
   * @param {number|null} targetSize - Optional target size (null = use source size)
   */
  generateBlurred(sourceFontName, targetFontName, targetSize = null) {
    if (PIXI.BitmapFont.available[targetFontName]) return;

    const sourceFont = PIXI.BitmapFont.available[sourceFontName];
    if (!sourceFont) return;

    const pageKeys = Object.keys(sourceFont.pageTextures);
    if (pageKeys.length === 0) return;
    const sourceTexture = sourceFont.pageTextures[pageKeys[0]];
    if (!sourceTexture || sourceTexture.width <= 0) return;

    const config = FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION;
    const blurStrength = (config.BLUR_STRENGTH ?? 18) * 0.5;
    const scale = targetSize ? targetSize / sourceFont.size : 1;
    const effectiveSize = targetSize || sourceFont.size;

    // Per-glyph blur: scale down + blur within original bounds
    const container = new PIXI.Container();
    const tempTextures = [];
    const charEntries = [];

    let atlasX = 0;
    let atlasY = 0;
    let rowHeight = 0;
    const padding = 2;
    const atlasWidth = Math.ceil(sourceTexture.width * scale) + padding * 2;
    let atlasHeight = Math.ceil(sourceTexture.height * scale) + padding * 2;

    for (const [charCode, charData] of Object.entries(sourceFont.chars)) {
      const charTexture = charData.texture;
      if (!charTexture || charTexture.width <= 0 || charTexture.height <= 0) {
        // Whitespace character — preserve metrics
        charEntries.push({
          id: charCode,
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          xoffset: Math.round(charData.xOffset * scale),
          yoffset: Math.round(charData.yOffset * scale),
          xadvance: Math.round(charData.xAdvance * scale),
        });
        continue;
      }

      const targetW = Math.max(1, Math.round(charTexture.frame.width * scale));
      const targetH = Math.max(1, Math.round(charTexture.frame.height * scale));

      // Step 1: Multi-pass downscale to target size (high quality)
      const scaledTex = this._multiPassDownscale(charTexture, targetW, targetH);

      // Step 2: Apply blur on the already-downscaled glyph
      // Add vertical padding so blur doesn't clip at edges
      const blurPad = Math.ceil(blurStrength * 2);
      const blurredSprite = new PIXI.Sprite(scaledTex);
      blurredSprite.anchor.set(0.5);
      blurredSprite.scale.set(config.SCALE_X ?? 1.0, config.SCALE_Y ?? 0.8);

      const blurFilter = new PIXI.BlurFilter();
      blurFilter.blurX = 0;
      blurFilter.blurY = blurStrength;
      blurFilter.quality = 8;
      blurredSprite.filters = [blurFilter];

      const renderW = targetW;
      const renderH = targetH + blurPad * 2;

      const tempTex = PIXI.RenderTexture.create({
        width: renderW,
        height: renderH,
        resolution: 1,
      });

      blurredSprite.position.set(renderW / 2, renderH / 2);
      this.renderer.render(blurredSprite, { renderTexture: tempTex });
      blurredSprite.filters = null;
      blurredSprite.destroy();
      scaledTex.destroy(true);

      // Row-packing layout
      if (atlasX + renderW + padding > atlasWidth) {
        atlasX = 0;
        atlasY += rowHeight + padding;
        rowHeight = 0;
      }

      const resultSprite = new PIXI.Sprite(tempTex);
      resultSprite.position.set(atlasX, atlasY);
      container.addChild(resultSprite);
      tempTextures.push(tempTex);

      charEntries.push({
        id: charCode,
        x: atlasX,
        y: atlasY,
        width: renderW,
        height: renderH,
        xoffset: Math.round(charData.xOffset * scale),
        yoffset: Math.round(charData.yOffset * scale) - blurPad,
        xadvance: Math.round(charData.xAdvance * scale),
      });

      atlasX += renderW + padding;
      rowHeight = Math.max(rowHeight, renderH);
    }

    atlasHeight = Math.max(atlasHeight, atlasY + rowHeight + padding);

    const atlasTexture = PIXI.RenderTexture.create({
      width: atlasWidth,
      height: atlasHeight,
      resolution: 1,
    });

    this.renderer.render(container, { renderTexture: atlasTexture });
    container.destroy({ children: true });
    for (const tex of tempTextures) {
      tex.destroy(true);
    }

    this._installFont(
      targetFontName,
      effectiveSize,
      Math.round(sourceFont.lineHeight * scale),
      atlasWidth,
      atlasHeight,
      charEntries,
      atlasTexture,
    );
  }

  /**
   * Render all glyphs from a source font at a target scale into a new atlas.
   * Uses row-packing layout for efficient texture space.
   * @private
   */
  _renderGlyphsToAtlas(sourceFont, scale) {
    const pageKeys = Object.keys(sourceFont.pageTextures);
    const sourceTexture = sourceFont.pageTextures[pageKeys[0]];

    const container = new PIXI.Container();
    const tempTextures = [];
    const charEntries = [];

    let atlasX = 0;
    let atlasY = 0;
    let rowHeight = 0;
    const padding = 2;
    const atlasWidth = Math.ceil(sourceTexture.width * scale) + padding * 2;
    let atlasHeight = Math.ceil(sourceTexture.height * scale) + padding * 2;

    for (const [charCode, charData] of Object.entries(sourceFont.chars)) {
      const charTexture = charData.texture;
      if (!charTexture || charTexture.width <= 0 || charTexture.height <= 0) {
        charEntries.push({
          id: charCode,
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          xoffset: Math.round(charData.xOffset * scale),
          yoffset: Math.round(charData.yOffset * scale),
          xadvance: Math.round(charData.xAdvance * scale),
        });
        continue;
      }

      const targetW = Math.max(1, Math.round(charTexture.frame.width * scale));
      const targetH = Math.max(1, Math.round(charTexture.frame.height * scale));

      // GPU-render glyph at exact target size using multi-pass downscaling
      // for high-quality results (avoids bilinear aliasing on large downscales)
      const tempTex = this._multiPassDownscale(charTexture, targetW, targetH);

      // Row-packing layout
      if (atlasX + targetW + padding > atlasWidth) {
        atlasX = 0;
        atlasY += rowHeight + padding;
        rowHeight = 0;
      }

      const resultSprite = new PIXI.Sprite(tempTex);
      resultSprite.position.set(atlasX, atlasY);
      container.addChild(resultSprite);
      tempTextures.push(tempTex);

      charEntries.push({
        id: charCode,
        x: atlasX,
        y: atlasY,
        width: targetW,
        height: targetH,
        xoffset: Math.round(charData.xOffset * scale),
        yoffset: Math.round(charData.yOffset * scale),
        xadvance: Math.round(charData.xAdvance * scale),
      });

      atlasX += targetW + padding;
      rowHeight = Math.max(rowHeight, targetH);
    }

    atlasHeight = Math.max(atlasHeight, atlasY + rowHeight + padding);

    const atlasTexture = PIXI.RenderTexture.create({
      width: atlasWidth,
      height: atlasHeight,
      resolution: 1,
    });

    this.renderer.render(container, { renderTexture: atlasTexture });
    container.destroy({ children: true });
    for (const tex of tempTextures) {
      tex.destroy(true);
    }

    return { atlasTexture, atlasWidth, atlasHeight, charEntries };
  }

  /**
   * Progressive multi-pass downscale for high-quality GPU rendering.
   * Halves the texture each pass (bilinear at 2:1 = perfect box filter),
   * then does a final fractional pass to reach the exact target size.
   * This avoids the aliasing/pixelation from single-pass large downscales.
   * @private
   */
  _multiPassDownscale(sourceTexture, targetW, targetH) {
    let currentW = sourceTexture.frame.width;
    let currentH = sourceTexture.frame.height;
    let currentTex = sourceTexture;
    const intermediates = [];

    // Progressive halving until within 2x of target
    while (currentW > targetW * 2 || currentH > targetH * 2) {
      const nextW = Math.max(targetW, Math.ceil(currentW / 2));
      const nextH = Math.max(targetH, Math.ceil(currentH / 2));

      const stepTex = PIXI.RenderTexture.create({
        width: nextW,
        height: nextH,
        resolution: 1,
      });

      const sprite = new PIXI.Sprite(currentTex);
      sprite.width = nextW;
      sprite.height = nextH;
      this.renderer.render(sprite, { renderTexture: stepTex });
      sprite.destroy();

      intermediates.push(stepTex);
      currentTex = stepTex;
      currentW = nextW;
      currentH = nextH;
    }

    // Final pass to exact target size
    const finalTex = PIXI.RenderTexture.create({
      width: targetW,
      height: targetH,
      resolution: 1,
    });

    const finalSprite = new PIXI.Sprite(currentTex);
    finalSprite.width = targetW;
    finalSprite.height = targetH;
    this.renderer.render(finalSprite, { renderTexture: finalTex });
    finalSprite.destroy();

    // Clean up intermediate textures
    for (const tex of intermediates) {
      tex.destroy(true);
    }

    return finalTex;
  }

  /**
   * Build BMFont XML and install a BitmapFont.
   * @private
   */
  _installFont(
    fontName,
    fontSize,
    lineHeight,
    scaleW,
    scaleH,
    charEntries,
    texture,
  ) {
    let charsXml = "";
    for (const c of charEntries) {
      charsXml += `<char id="${c.id}" x="${c.x}" y="${c.y}" width="${c.width}" height="${c.height}" xoffset="${c.xoffset}" yoffset="${c.yoffset}" xadvance="${c.xadvance}" page="0" chnl="15"/>`;
    }

    const xml = [
      `<?xml version="1.0" encoding="UTF-8"?>`,
      `<font>`,
      `<info face="${fontName}" size="${fontSize}"/>`,
      `<common lineHeight="${lineHeight}" base="${Math.round(fontSize * 0.775)}" scaleW="${scaleW}" scaleH="${scaleH}" pages="1"/>`,
      `<pages><page id="0" file=""/></pages>`,
      `<chars count="${charEntries.length}">${charsXml}</chars>`,
      `</font>`,
    ].join("");

    PIXI.BitmapFont.install(xml, texture);
  }
}

export default FontGenerator;
